import React, { useState } from 'react';
import { Smile, List } from 'lucide-react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useAIAgentSidebarState } from '@/hooks/ai-agents/useAIAgentSidebarState';

// Common emoji categories for the picker
const emojiCategories = {
  faces: ['😀', '😃', '😄', '😁', '😅', '😂', '🤣', '😊', '😇', '🙂', '🙃', '😉', '😌', '😍', '🥰', '😘', '😗', '😙', '😚', '😋', '😛', '😝', '😜', '🤪', '🤨', '🧐', '🤓', '😎', '🥸', '🤩', '🥳'],
  reactions: ['👍', '👎', '👌', '🤌', '🤏', '✌️', '🤞', '🤟', '🤘', '🤙', '👈', '👉', '👆', '🖕', '👇', '☝️', '👋', '🤚', '🖐️', '✋', '🖖', '👏', '🙌', '🤲', '🤝', '🙏'],
  hearts: ['❤️', '🧡', '💛', '💚', '💙', '💜', '🖤', '🤍', '🤎', '💔', '❣️', '💕', '💞', '💓', '💗', '💖', '💘', '💝', '💟'],
  celebration: ['🎉', '🎊', '🎈', '🎁', '🎀', '🎂', '🍰', '🧁', '🥳', '🎆', '🎇', '✨', '🎃', '🎄', '🎋', '🎍', '🎎', '🎏', '🎐', '🎑', '🧧', '🎗️', '🎟️', '🎫']
};

interface EmojiPickerProps {
  onEmojiSelect: (emoji: string) => void;
}

const EmojiPicker: React.FC<EmojiPickerProps> = ({ onEmojiSelect }) => {
  const [activeCategory, setActiveCategory] = useState<keyof typeof emojiCategories>('faces');

  return (
    <div className="w-80 p-4">
      <div className="mb-3">
        <h3 className="text-sm font-medium text-foreground mb-2">Choose an emoji</h3>
        
        {/* Category tabs */}
        <div className="flex gap-1 mb-3">
          {Object.keys(emojiCategories).map((category) => (
            <button
              key={category}
              onClick={() => setActiveCategory(category as keyof typeof emojiCategories)}
              className={`px-2 py-1 text-xs rounded-md transition-colors ${
                activeCategory === category
                  ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
                  : 'hover:bg-slate-100 dark:hover:bg-slate-700/50 text-slate-600 dark:text-slate-400'
              }`}
            >
              {category.charAt(0).toUpperCase() + category.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Emoji grid */}
      <div className="grid grid-cols-8 gap-1 max-h-48 overflow-y-auto">
        {emojiCategories[activeCategory].map((emoji) => (
          <button
            key={emoji}
            onClick={() => onEmojiSelect(emoji)}
            className="p-2 text-lg hover:bg-slate-100 dark:hover:bg-slate-700/50 rounded-md transition-colors"
          >
            {emoji}
          </button>
        ))}
      </div>
    </div>
  );
};

export const ActionButtons: React.FC = () => {
  const [isEmojiPickerOpen, setIsEmojiPickerOpen] = useState(false);

  const handleEmojiSelect = (emoji: string) => {
    // Get the message input element and insert emoji at cursor position
    const messageInput = document.querySelector('[data-copilot-input="true"]') as HTMLTextAreaElement;
    if (messageInput) {
      const start = messageInput.selectionStart;
      const end = messageInput.selectionEnd;
      const currentValue = messageInput.value;
      
      // Insert emoji at cursor position
      const newValue = currentValue.substring(0, start) + emoji + currentValue.substring(end);
      
      // Update the input value
      messageInput.value = newValue;
      
      // Trigger input event to update React state
      const event = new Event('input', { bubbles: true });
      messageInput.dispatchEvent(event);
      
      // Set cursor position after the emoji
      const newCursorPosition = start + emoji.length;
      setTimeout(() => {
        messageInput.setSelectionRange(newCursorPosition, newCursorPosition);
        messageInput.focus();
      }, 0);
    }
    
    setIsEmojiPickerOpen(false);
  };

  return (
    <>
      <Popover open={isEmojiPickerOpen} onOpenChange={setIsEmojiPickerOpen}>
        <PopoverTrigger asChild>
          <motion.button 
            type="button"
            className="p-1.5 rounded-full hover:bg-slate-100 dark:hover:bg-slate-700/70 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
            title="Add emoji"
          >
            <Smile size={18} strokeWidth={1.5} />
          </motion.button>
        </PopoverTrigger>
        <PopoverContent 
          className="w-auto p-0 bg-white/95 dark:bg-gray-800/95 backdrop-blur-md shadow-lg border border-slate-200 dark:border-slate-700" 
          align="end"
          side="top"
        >
          <EmojiPicker onEmojiSelect={handleEmojiSelect} />
        </PopoverContent>
      </Popover>
    </>
  );
};

interface ThreadButtonProps {
  onClose?: () => void;
}

export const ThreadButton: React.FC<ThreadButtonProps> = ({ onClose }) => {
  const { switchToThreadsTab } = useAIAgentSidebarState();

  const handleThreadsClick = () => {
    console.log('Thread button clicked - switching to threads tab using centralized state');
    
    // ✅ CLEAN: Use centralized state management directly
    switchToThreadsTab();
  };

  return (
    <motion.button 
      type="button"
      onClick={handleThreadsClick}
      className="p-1.5 rounded-full hover:bg-slate-100 dark:hover:bg-slate-700/70 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
      whileHover={{ scale: 1.1 }}
      whileTap={{ scale: 0.95 }}
      title="Threads"
    >
      <List size={18} strokeWidth={1.5} />
    </motion.button>
  );
};

export default { ActionButtons, ThreadButton };
